import {action, makeObservable, observable} from 'mobx';
import {ApiRepository, ValidationError} from '../base/api-repository';
import {routes} from '../base/routes';
import {RouterStore} from 'mobx-router';
import {RootStore} from '../base/root-store';
import {formatToYearAndMonthAndDayWithDateInput, isDateInFutureForModal, localTimezone} from '../base/dateUtils';
import {SessionStore} from '../user/session-store';
import {LabelStore} from '../base/label-store';

export class CompanySignUpStore{
    @observable
    stage: 'signup' | 'agreement' = 'agreement';
    @observable
    state: 'loading' | 'error' | 'idle' = 'idle';
    @observable
    companyId: string = '';
    @observable
    showValidation: boolean = false;
    @observable
    invalidEmail: boolean = false;

    constructor(private api: ApiRepository, private sessionStore: SessionStore, private router: RouterStore<RootStore>, public labelStore: LabelStore) {
        makeObservable(this);
    }

    @action
    onEnter(): void {
        this.sessionStore.state = 'no-session';
        localStorage.clear();
        this.stage = 'signup';
        this.showValidation = false;
        this.invalidEmail = false;
    }

    @action
    async signUpCompany(name: string, address: string, regNumber: string, startDate: Date, employeeFirstName: string, employeeLastName: string, employeeEmail: string) : Promise<void> {
        await this.load(async () => {
            const timezone = localTimezone !== undefined || localTimezone !== '' ? localTimezone : undefined;
            const request: CompanySignUpRequest = {
                companyName: name,
                companyAddress: address,
                companyRegNumber: regNumber,
                surveyStartDate: formatToYearAndMonthAndDayWithDateInput(startDate as Date),
                firstName: employeeFirstName,
                lastName: employeeLastName,
                emailAddress: employeeEmail,
                questionsTimezone: timezone,
            };

            if (this.isSignUpRequestValid(request)) {
                try {
                    const signUpResponse = await this.api.post<CompanySignUpResponse>(`/signup/company`, request);

                    this.stage = 'agreement';
                    this.companyId = signUpResponse.companyId;
                } catch (e: unknown) {
                    if ((e as ValidationError).name === 'ValidationError') {
                        this.invalidEmail = true;
                    } else {
                        throw e;
                    }
                }
            } else {
                this.showValidation = true;
            }
        });
    }

    @action
    async finaliseSignUp(id: string) : Promise<void> {
        const finaliseCompanySignUp: FinaliseCompanySignUp = {
            companyId: id,
        };

        await this.api.post<void>(`/signup/company/finalise`, finaliseCompanySignUp);

        await this.router.goTo(routes.company);
    }

    @action
    async load(process: () => void): Promise<void> {
        try {
            this.state = 'loading';
            await process();
            this.state = 'idle';
        } catch (e) {
            this.state = 'error';
        }
    }

    isSignUpRequestValid(request: CompanySignUpRequest): boolean {
        return !this.checkEmail(request.emailAddress) &&
            !this.checkIfMissing(request.companyName) &&
            !this.checkifMissingOrTooShort(request.companyAddress) &&
            !this.checkStartDate(new Date(request.surveyStartDate)) &&
            !this.checkifMissingOrTooShort(request.companyRegNumber) &&
            !this.checkIfMissing(request.firstName) &&
            !this.checkIfMissing(request.lastName);
    }

    checkIfMissing(value: string): undefined | 'missing' {
        if (value === '') {
            return 'missing';
        } else {
            return undefined;
        }
    }

    checkifMissingOrTooShort(value: string): undefined | 'missing' | 'too-short' {
        if (value === '') {
            return 'missing';
        } else if (value.length < 8) {
            return 'too-short';
        } else {
            return undefined;
        }
    }

    checkStartDate(value: Date): undefined | 'missing' | 'not-in-future' {
        if (value === undefined) {
            return 'missing';
        } else if (!isDateInFutureForModal(value)) {
            return 'not-in-future';
        } else {
            return undefined;
        }
    }

    checkEmail(value: string): undefined | 'missing' | 'invalid-email-format' {
        const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+[^\s@]$/;

        if (value === '') {
            return 'missing';
        } else if (!validEmail.test(value)) {
            return 'invalid-email-format';
        } else {
            return undefined;
        }
    }
}

interface CompanySignUpResponse{
    companyId: string;
}

interface CompanySignUpRequest {
    companyName: string,
    companyAddress: string,
    companyRegNumber: string,
    surveyStartDate: string,
    firstName: string,
    lastName: string,
    emailAddress: string,
    questionsTimezone?: string | undefined
}

interface FinaliseCompanySignUp {
    companyId: string;
}

